import React from 'react';
import { ORG_PLAN_NOTICE } from '../../checkout/purchaseEligibility';

export const PLAN_HELD_NOTICE =
  'This part is not included in the exam purchase on your account. Purchases are not upgraded or changed.';
export const SEE_PLANS_NOTICE = 'This part is not in your plan yet. See plans to find out what each one includes.';

/**
 * Whether the account holds a paid exam purchase. Decided from the server's
 * own `access` object (`has_paid`, `owned`), never from `active_plan`: a user
 * who chose Beginnings keeps `active_plan` free, and an organisation member's
 * is `org_full`, so that string says nothing reliable about what they own. A
 * user stored without `access.owned` owns nothing.
 *
 * @param {object|null|undefined} user
 * @returns {boolean}
 */
export function holdsPaidExam(user) {
  const access = user && user.access;
  if (!access || typeof access !== 'object') return false;
  if (access.has_paid === true) return true;
  const owned = access.owned;
  if (!owned || typeof owned !== 'object') return false;
  if (owned.has_paid === true || owned.all_tracks === true) return true;
  return (
    Array.isArray(owned.exams) &&
    owned.exams.some((held) => held && held.tier && held.tier !== 'beginnings')
  );
}

const OVERLAY_STYLE = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'color-mix(in srgb, var(--iv-band) 60%, transparent)',
  backdropFilter: 'blur(4px)',
  WebkitBackdropFilter: 'blur(4px)',
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '20px',
};

const PANEL_STYLE = {
  background: 'var(--iv-bg)',
  border: '1px solid var(--iv-line)',
  borderRadius: '16px',
  maxWidth: '680px',
  width: '100%',
  padding: '32px',
  boxShadow: 'none',
  fontFamily: 'var(--iv-font-body)',
};

const COPY_STYLE = { fontSize: '14px', color: 'var(--iv-muted)', lineHeight: '1.5', marginBottom: '24px' };

/**
 * The one notice every locked part opens. It never shows a price and never
 * starts a checkout: a holder of a paid exam purchase has no upgrade path, an
 * organisation member's plan comes from the organisation, and everyone else
 * is sent to the pricing page.
 */
export default function LockedPartNotice({ title, tierLabel, user, mayPurchase, onSeePlans, onClose }) {
  const holder = holdsPaidExam(user);
  return (
    <div style={OVERLAY_STYLE}>
      <div style={PANEL_STYLE}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px' }}>
          <div>
            <span style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '0.12em', color: 'var(--iv-danger)', textTransform: 'uppercase' }}>
              PLAN TIER UNLOCK REQUIRED
            </span>
            <h2 style={{ fontSize: '24px', fontWeight: '700', color: 'var(--iv-body)', margin: '6px 0 0 0' }}>
              {title}
            </h2>
          </div>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: 'var(--iv-muted)', padding: '0 4px', fontWeight: '600' }}
          >
            ✕
          </button>
        </div>

        {!mayPurchase ? (
          <p style={COPY_STYLE}>{ORG_PLAN_NOTICE}</p>
        ) : holder ? (
          <p style={COPY_STYLE}>
            This part belongs to the <strong>{tierLabel || 'Foundations'}</strong> folder structure. {PLAN_HELD_NOTICE}
          </p>
        ) : (
          <>
            <p style={COPY_STYLE}>
              This part belongs to the <strong>{tierLabel || 'Foundations'}</strong> folder structure. {SEE_PLANS_NOTICE}
            </p>
            <div style={{ marginBottom: '24px' }}>
              <button
                type="button"
                onClick={onSeePlans}
                style={{ background: 'var(--iv-btn)', color: 'var(--iv-btn-fg)', border: 'none', borderRadius: '8px', padding: '10px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', boxShadow: 'none' }}
              >
                See plans
              </button>
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button
            type="button"
            onClick={onClose}
            style={{ background: 'var(--iv-fill)', border: 'none', borderRadius: '8px', padding: '10px 20px', fontSize: '13px', fontWeight: '600', color: 'var(--iv-head)', cursor: 'pointer', boxShadow: 'none' }}
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
